import { useState, useEffect, useRef } from "react";
import type { XPostTemplateResponse } from "../../../types/crm";
import {
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from "../../../services/crmXPosts";
import "./XPostTemplateAdminPage.css";

const AVAILABLE_VARIABLES = [
  "firstName", "lastName", "position", "city", "state",
  "gradYear", "school", "height", "weight", "bats", "throws", "gpa", "travelTeam",
];

const SAMPLE_DATA: Record<string, string> = {
  firstName: "John",
  lastName: "Smith",
  position: "Shortstop",
  city: "Houston",
  state: "TX",
  gradYear: "2027",
  school: "Central High",
  height: "6'1\"",
  weight: "185",
  bats: "R",
  throws: "R",
  gpa: "3.8",
  travelTeam: "Texas Bombers",
};

export const XPostTemplateAdminPage = () => {
  const [templates, setTemplates] = useState<XPostTemplateResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formBody, setFormBody] = useState("");
  const [formDefault, setFormDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPosRef = useRef<number | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getTemplates();
      setTemplates(data);
    } catch {
      setError("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (t: XPostTemplateResponse) => {
    setEditingId(t.id);
    setFormName(t.name);
    setFormBody(t.bodyTemplate);
    setFormDefault(t.isDefault);
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setFormName("");
    setFormBody("");
    setFormDefault(false);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formBody.trim()) {
      setError("Name and body are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await updateTemplate(editingId, { name: formName, bodyTemplate: formBody, isDefault: formDefault });
      } else {
        await createTemplate({ name: formName, bodyTemplate: formBody, isDefault: formDefault });
      }
      setShowForm(false);
      setEditingId(null);
      await loadTemplates();
    } catch {
      setError("Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await deleteTemplate(id);
      await loadTemplates();
    } catch {
      setError("Failed to delete template.");
    }
  };

  const insertVariable = (varName: string) => {
    const varText = `{${varName}}`;
    const pos = cursorPosRef.current ?? formBody.length;
    const newBody = formBody.slice(0, pos) + varText + formBody.slice(pos);
    setFormBody(newBody);
    const newPos = pos + varText.length;
    cursorPosRef.current = newPos;
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    });
  };

  const getPreview = () => {
    return formBody.replace(/\{(\w+)\}/g, (_, v) => SAMPLE_DATA[v] || `{${v}}`);
  };

  return (
    <div className="xpost-templates-page">
      <div className="xpost-templates-page__header">
        <h2>X Post Templates</h2>
        <button className="xpost-templates-page__new-btn" onClick={handleNew}>
          + New Template
        </button>
      </div>

      {error && <div className="xpost-templates-page__error">{error}</div>}

      {showForm && (
        <div className="xpost-templates-page__form">
          <div className="xpost-templates-page__form-field">
            <label htmlFor="x-post-template-admin-page-name-0">Name</label>
            <input id="x-post-template-admin-page-name-0"
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Template name"
            />
          </div>

          <div className="xpost-templates-page__form-field">
            <label htmlFor="x-post-template-admin-page-body-template-1">Body Template</label>
            <textarea id="x-post-template-admin-page-body-template-1"
              ref={textareaRef}
              value={formBody}
              onChange={(e) => {
                setFormBody(e.target.value);
                cursorPosRef.current = e.target.selectionStart;
              }}
              onSelect={(e) => {
                cursorPosRef.current = (e.target as HTMLTextAreaElement).selectionStart;
              }}
              placeholder="Check out this {position}, {firstName} {lastName}, from {city}, {state}"
              rows={3}
            />
            <div className="xpost-templates-page__variables">
              {AVAILABLE_VARIABLES.map((v) => (
                <button
                  key={v}
                  className="xpost-templates-page__var-chip"
                  onClick={() => insertVariable(v)}
                  type="button"
                >
                  {`{${v}}`}
                </button>
              ))}
            </div>
          </div>

          {formBody && (
            <div className="xpost-templates-page__preview">
              <span id="x-post-template-admin-page-preview-0">Preview</span>
              <div className="xpost-templates-page__preview-text" role="group" aria-labelledby="x-post-template-admin-page-preview-0">{getPreview()}</div>
              <span className="xpost-templates-page__preview-chars">
                {getPreview().length}/280 characters
              </span>
            </div>
          )}

          <div className="xpost-templates-page__form-field xpost-templates-page__form-checkbox">
            <label>
              <input
                type="checkbox"
                checked={formDefault}
                onChange={(e) => setFormDefault(e.target.checked)}
              />
              Set as default template
            </label>
          </div>

          <div className="xpost-templates-page__form-actions">
            <button onClick={handleCancel} disabled={saving}>Cancel</button>
            <button
              className="xpost-templates-page__save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : editingId ? "Update" : "Create"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="xpost-templates-page__loading">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="xpost-templates-page__empty">
          No templates yet. Create one to get started.
        </div>
      ) : (
        <div className="xpost-templates-page__list">
          {templates.map((t) => (
            <div key={t.id} className="xpost-templates-page__item">
              <div className="xpost-templates-page__item-header">
                <span className="xpost-templates-page__item-name">{t.name}</span>
                <div className="xpost-templates-page__item-badges">
                  {t.isDefault && (
                    <span className="xpost-templates-page__badge xpost-templates-page__badge--default">
                      Default
                    </span>
                  )}
                  {t.isSystemDefault && (
                    <span className="xpost-templates-page__badge xpost-templates-page__badge--system">
                      System
                    </span>
                  )}
                </div>
              </div>
              <div className="xpost-templates-page__item-body">{t.bodyTemplate}</div>
              {!t.isSystemDefault && (
                <div className="xpost-templates-page__item-actions">
                  <button onClick={() => handleEdit(t)}>Edit</button>
                  <button
                    className="xpost-templates-page__delete-btn"
                    onClick={() => handleDelete(t.id)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
